import {
  faBed,
  faBus,
  faGlobe,
  faPlane,
  faShieldAlt,
  faStar,
  faUtensils,
  IconDefinition
} from '@fortawesome/free-solid-svg-icons';
import {
  ApiComponent,
  ApiComponentType,
  ApiExtraService,
  ApiFlightFare,
  ApiFlightItem,
  ApiFlightSegment,
  ApiHotel,
  ApiImage,
  ApiInsurance,
  ApiItem,
  ApiItemType,
  ApiMedia,
  ApiPackageDetails,
  ApiPackageModel,
  ApiPrice,
  ApiTransfer,
  ApiTravelDateCandidate
} from '@ibe/api';
import dayjs from 'dayjs';
import { cloneDeep } from 'lodash-es';
import { ItemTypesEnum } from '../../../../Models/ItemTypesEnum';

abstract class PackageCustomizerUtil {
  public static getDates(startDate: string, endDate: string): string[] {
    const daysToAdd = 1;
    const dates: string[] = [];
    let start = dayjs(startDate);
    const end = dayjs(endDate);
    while (start.isBefore(end)) {
      dates.push(start.format('ddd MMM DD, YYYY '));
      start = start.add(daysToAdd, 'day');
    }
    dates.push(start.format('ddd MMM DD, YYYY '));
    return dates;
  }

  private static getProperArrayOfItems(component: ApiComponent): ApiItem[] | undefined {
    let arrayToBeSearched;

    if (component.selectedItems.length !== 0) {
      arrayToBeSearched = component.selectedItems;
    } else if (component.selectableItems.length !== 0) {
      arrayToBeSearched = component.selectableItems;
    }

    return arrayToBeSearched;
  }

  public static getFlightOriginFromComponent(component: ApiComponent): string | null {
    if (component.selectedItems) {
      const flightItem = (component.selectedItems as ApiFlightItem[]).find(
        item => item.segment && item.segment[0].origin
      );
      return flightItem?.segment[0].origin.code || null;
    }
    return null;
  }

  public static getFlightDestinationFromComponent(component: ApiComponent): string | null {
    if (component.selectedItems) {
      const flightItem = (component.selectedItems as ApiFlightItem[]).find(
        item => item.segment && item.segment[item.segment.length - 1].origin
      );
      return flightItem?.segment[flightItem.segment.length - 1].destination.code || null;
    }
    return null;
  }

  public static getDepartureDate(component: ApiComponent): string | null {
    const arrayToBeSearched = this.getProperArrayOfItems(component);

    const flightItem = (arrayToBeSearched as ApiFlightItem[]).find(
      item => item.segment && item.segment[0].departure
    );
    return flightItem?.segment[0].departure || null;
  }

  public static getArrivalDate(component: ApiComponent): string | null {
    const arrayToBeSearched = this.getProperArrayOfItems(component);

    const flightItem = (arrayToBeSearched as ApiFlightItem[]).find(
      item => item.segment && item.segment[item.segment.length - 1].arrival
    );
    return flightItem?.segment[flightItem.segment.length - 1].arrival || null;
  }

  public static getProperIconForGivenComponent(
    component: ApiComponent
  ): IconDefinition | undefined {
    if (component.itemType === ApiItemType.FLIGHT) {
      return faPlane;
    }
    if (component.itemType === ApiItemType.HOTEL) {
      return faBed;
    }
    if (component.itemType === ApiItemType.TOUR) {
      return faGlobe;
    }
    if (component.itemType === ApiItemType.INSURANCE) {
      return faShieldAlt;
    }
    if (component.itemType === ApiItemType.TRANSFER) {
      return faBus;
    }
    if (component.itemType === ApiItemType.EXTRA) {
      if (component.subItemType === ApiItemType.MEETANDGREET) {
        return faStar;
      }
      if (component.subItemType === ApiItemType.LOUNGEACCESS) {
        return faStar;
      }
      if (component.subItemType === ApiItemType.DINING) {
        return faUtensils;
      }
      return faStar;
    }
    return undefined;
  }

  public static getItemsTypeFromComponent(component: ApiComponent): string {
    if (component.itemType) {
      return component.itemType;
    }

    const arrayToBeSearched = this.getProperArrayOfItems(component);

    return arrayToBeSearched ? arrayToBeSearched[0].itemType : '';
  }

  public static getItemsSubTypeFromComponentItems(component: ApiComponent): string {
    return component.subItemType || component.itemType || '';
  }

  public static getTravelDateById(
    packageModel: ApiPackageModel | undefined,
    id: string | undefined
  ): ApiTravelDateCandidate | undefined {
    if (packageModel && id) {
      if (packageModel.travelDates) {
        return packageModel.travelDates.find(travelDate => travelDate.id === id);
      }
    }
    return undefined;
  }

  // todo: all of this only works with one unit
  public static getItemPrice(item: ApiItem): ApiPrice | undefined {
    if (item == null) {
      return undefined;
    }

    const type = item.type || null;

    if (type === ItemTypesEnum.FLIGHT) {
      const flightFares: ApiFlightFare[] = (item as ApiFlightItem).fare || [];
      return flightFares.length > 0 ? flightFares[0].price : undefined;
    }
    if (type === ItemTypesEnum.FLIGHT_FARE) {
      return item.price;
    }
    if (type === ItemTypesEnum.TRANSFER) {
      return (item as ApiTransfer).units[0].price;
    }
    if (type === ItemTypesEnum.HOTEL) {
      return (item as ApiHotel).rooms[0].roomrates[0].price;
    }
    if (type === ItemTypesEnum.EXCURSION) {
      return (item as ApiExtraService).extraUnits[0].price;
    }
    if (type === ItemTypesEnum.INSURANCE) {
      return (item as ApiInsurance).units[0].price;
    }
    if (type === ItemTypesEnum.EXTRA) {
      return (item as ApiExtraService).extraUnits[0].price;
    }

    return undefined;
  }

  public static getItemDeltaPrice(item: ApiItem, ref: ApiPrice | undefined): ApiPrice {
    const itemPrice = item.price;

    return this.getDeltaPrice(itemPrice, ref);
  }

  public static getDeltaPrice(itemPrice: ApiPrice, ref: ApiPrice | undefined): ApiPrice {
    if (ref && itemPrice) {
      const deltaPrice: ApiPrice = cloneDeep(itemPrice);

      if (deltaPrice) {
        deltaPrice.basePrice -= ref.basePrice;
        deltaPrice.finalPrice -= ref.finalPrice;

        return deltaPrice;
      }
    }

    return itemPrice;
  }

  public static hasMultiSelection(component: ApiComponent): boolean {
    return PackageCustomizerUtil.getItemsTypeFromComponent(component) === ItemTypesEnum.EXTRA;
  }

  public static getDaysForComponent(component: ApiComponent, packageStartDate: string): string {
    const startDate = dayjs(component.startDate);
    const endDate = dayjs(component.endDate);
    const componentDuration: number = endDate.diff(startDate, 'day');
    const packageStart = dayjs(packageStartDate);
    const startDay: number = startDate.diff(packageStart, 'day') + 1;
    return (
      startDay.toString() +
      (componentDuration > 0 ? `-${(startDay + componentDuration).toString()}` : '')
    );
  }

  public static getLowestPriceOfSelectable(component: ApiComponent): ApiPrice | undefined {
    let minPrice = component.selectableItems.find(
      item => !!PackageCustomizerUtil.getItemPrice(item)
    )?.price;

    component.selectableItems?.forEach(item => {
      const price = PackageCustomizerUtil.getItemPrice(item);
      if (price && minPrice && price.finalPrice < minPrice.finalPrice) {
        minPrice = price;
      }
    });

    return minPrice;
  }

  static getMediaForComponent(component: ApiComponent): ApiMedia | undefined {
    if (!component || !component.selectableItems || !component.selectableItems[0]) {
      return undefined;
    }

    const item = component.selectableItems[0];

    if (item.type === ItemTypesEnum.FLIGHT) {
      // @TODO add image to ApiFare
      return undefined;
    }
    if (item.type === ItemTypesEnum.TRANSFER) {
      return (item as ApiTransfer).media;
    }
    if (item.type === ItemTypesEnum.HOTEL) {
      return (item as ApiHotel).media;
    }
    if (item.type === ItemTypesEnum.EXCURSION) {
      return (item as ApiExtraService).media;
    }
    if (item.type === ItemTypesEnum.INSURANCE) {
      return (item as ApiInsurance).media;
    }
    if (item.type === ItemTypesEnum.EXTRA) {
      return (item as ApiExtraService).media;
    }
    return undefined;
  }

  static getImageForComponent(component: ApiComponent): ApiImage | undefined {
    const media = this.getMediaForComponent(component);
    if (media && media.mainImage && media.mainImage.small) {
      return media.mainImage.small;
    }
    if (media && media.mainImage && media.mainImage.offer) {
      return media.mainImage.offer;
    }
    if (media && media.mainImage && media.mainImage.extraSmall) {
      return media.mainImage.extraSmall;
    }
    if (media && media.mainImage && media.mainImage.large) {
      return media.mainImage.large;
    }
    if (media && media.mainImage && media.mainImage.extraLarge) {
      return media.mainImage.extraLarge;
    }
    return undefined;
  }

  static getStartItemDate(component: ApiComponent): string | undefined {
    if (!component || !component.selectableItems || !component.selectableItems[0]) {
      return undefined;
    }

    const item = component.selectableItems[0];

    if (item.type === ItemTypesEnum.FLIGHT) {
      const segment: ApiFlightSegment[] = (item as ApiFlightItem).segment || [];
      return segment.length > 0 ? segment[0].departure : undefined;
    }
    if (item.type === ItemTypesEnum.TRANSFER) {
      return (item as ApiTransfer).start;
    }
    if (item.type === ItemTypesEnum.HOTEL) {
      return (item as ApiHotel).start;
    }
    if (item.type === ItemTypesEnum.EXCURSION) {
      return (item as ApiExtraService).start;
    }
    if (item.type === ItemTypesEnum.INSURANCE) {
      return (item as ApiInsurance).start;
    }
    if (item.type === ItemTypesEnum.EXTRA) {
      return (item as ApiExtraService).start;
    }
    return undefined;
  }

  static getSelectedItems(packageDetails: ApiPackageDetails | null): ApiItem[] {
    const items: ApiItem[] = [];
    if (!packageDetails) {
      return items;
    }
    for (
      let indexComponent = 0;
      indexComponent < packageDetails.components.length;
      indexComponent += 1
    ) {
      const component = packageDetails.components[indexComponent];
      if (component) {
        for (let indexItems = 0; indexItems < component.selectedItems.length; indexItems += 1) {
          items.push(component.selectedItems[indexItems]);
        }
      }
    }
    return items;
  }

  static isFlightSelected(packageDetails: ApiPackageDetails | null): boolean {
    if (!packageDetails) {
      return false;
    }
    const flightComponents = packageDetails.components.filter(
      comp => comp.itemType === ApiItemType.FLIGHT
    );
    for (let indexComponent = 0; indexComponent < flightComponents.length; indexComponent += 1) {
      const component = flightComponents[indexComponent];
      if (component.selectedItems.length < 1 && component.relatedComponentId === undefined) {
        return false;
      }
      const relatedComponent = flightComponents.find(c => c.id === component.relatedComponentId);
      if (relatedComponent && relatedComponent.selectedItems.length < 1) {
        return false;
      }
    }
    return true;
  }

  static isAllRequiredAdded(packageDetails: ApiPackageDetails | null): boolean {
    if (!packageDetails) {
      return false;
    }
    for (
      let indexComponent = 0;
      indexComponent < packageDetails.components.length;
      indexComponent += 1
    ) {
      const component = packageDetails.components[indexComponent];
      if (
        component.componentType === ApiComponentType.REQUIRED ||
        component.componentType === ApiComponentType.MAIN
      ) {
        if (component.selectedItems.length < 1 && component.relatedComponentId === undefined) {
          return false;
        }
        const relatedComponent = packageDetails.components.find(
          c => c.id === component.relatedComponentId
        );
        if (relatedComponent && relatedComponent.selectedItems.length < 1) {
          return false;
        }
      }
    }
    return true;
  }

  static isComponentSelected(
    component: ApiComponent,
    allComponents: ApiComponent[]
  ): boolean | undefined {
    const relatedComponent = allComponents.find(c => c.id === component.relatedComponentId);
    return (
      component.selectedItems.length > 0 ||
      (relatedComponent && relatedComponent.selectedItems.length > 0)
    );
  }

  static getUnselectedOptionalComponents(components: ApiComponent[]): ApiComponent[] {
    const componentsToShow: ApiComponent[] = [];
    if (!components) {
      return componentsToShow;
    }
    for (let index = 0; index < components.length; index += 1) {
      if (
        components[index].componentType === ApiComponentType.OPTIONAL &&
        !PackageCustomizerUtil.isComponentSelected(components[index], components)
      ) {
        componentsToShow.push(components[index]);
      }
    }
    return componentsToShow;
  }

  static isFlightChosen(flightComponent: ApiComponent): boolean {
    return (
      flightComponent && flightComponent.selectedItems && flightComponent.selectedItems.length > 0
    );
  }

  static hasOptionalComponents(packageDetails: ApiPackageDetails | undefined): boolean {
    if (!packageDetails) {
      return false;
    }
    return packageDetails.components.some(
      component => component.componentType === ApiComponentType.OPTIONAL
    );
  }
}

export default PackageCustomizerUtil;
